<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.cohorts') }}</h3>
                </div>
                <div class="col-6 text-right" :class="{'pr-0': canImport || canDeleteAll}">

                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="primary" size="sm" @click="ctrl.create()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('cohorts.new') }}</span>
                    </base-button>

                    <actions-dropdown :actions="mainActions()" v-if="canImport || canDeleteAll">
                    </actions-dropdown>
                </div>
            </div>
        </div>

        <div class="pt-2">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="filterChange"
                    ref="table">

                <el-table-column :label="$t('common.name')"
                    sortable="custom" prop="name"
                >
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="openLearners(row)">
                                {{ row.name }}
                            </a>
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.status')"
                    column-key="status"
                    :filters="filtering.filteringValues(allStatusOptions)"
                    :filtered-value="filters.status"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <badge :type="statusColor(row.status)">{{ statusLabel(row.status) }}</badge>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.owner')"
                    column-key="owner"
                    :filters="filtering.filteringValues(owners)"
                    :filtered-value="filters.owner"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        {{ owners[row.owner_id] }}
                    </template>
                </el-table-column>

                <el-table-column :label="$t('cohorts.members')">
                    <template v-slot="{row}">
                        <a href="#" @click.prevent="openLearners(row)">
                            <small>
                                {{ $t('common.learners') }}: {{ row.learners_count }}
                            </small>
                        </a>
                        <br>
                        <a href="#" @click.prevent="openInstructors(row)">
                            <small>
                                {{ $t('common.instructors') }}: {{ row.instructors_count }}
                            </small>
                        </a>
                    </template>
                </el-table-column>

                <el-table-column width="80"
                    :label="$t('common.origin')"
                    column-key="origin"
                    :filters="origins"
                    :filtered-value="filters.origin"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <badge type="warning" v-if="row.origin == 'local'">{{ row.origin }}</badge>
                        <badge type="secondary" v-else>{{ row.origin }}</badge>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="100">
                    <template v-slot="{row}">
                        <div class="table-actions">

                            <a href="#" class="table-action text-danger" @click.prevent="ctrl.delete(row.id)" v-if="canEdit(row)">
                                <i class="tim-icons icon-simple-remove"></i>
                            </a>

                            <a href="#" class="table-action text-primary" @click.prevent="ctrl.update(row.id)" v-if="canEdit(row)">
                                <i class="tim-icons icon-pencil"></i>
                            </a>

                        </div>
                    </template>
                </el-table-column>
                
                <el-table-column align="right" width="50" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit"
                :title="$t('common.cohort')"
                :loading="ctrl.form.loading"
            >
                <cohort-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :owners="owners"
                    :status-options="statusOptions"
                    @next="next = true"
                >
                </cohort-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('cohorts.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmArchive">
                <p>{{ $t('status.archive-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmRestore">
                <p>{{ $t('status.restore-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import CohortMapping from './CohortMapping'
    import CohortForm from './CohortForm'

    export default {

        components: {
            CohortForm
        },

        data() {
            let mapping = new CohortMapping()
            let filtering = new CrudFiltering('crud_cohorts')
            return {
                loaded: false,
                mapping,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/cohorts',
                    mapData: mapping.mapData,
                    unmapData: mapping.unmapData,
                    modals: ['confirmArchive', 'confirmRestore'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['name'],
                        include: ['status_options', 'owners'],
                        accessors: ['learners_count', 'instructors_count']
                    },
                    callbacks: {
                        created: this.thenOpen,
                        updated: this.thenOpen,
                        listed: this.listed
                    },
                    errors: {
                        '423-delete': 'cohorts.423-delete',
                    }
                }),
                owners: {},
                allStatusOptions: {},
                statusOptions: {},
                next: false,
                search: '',
                filters: {},
            }
        },

        computed: {

            canDeleteAll() {
                return this.$auth.hasPermission('admin.delete-personal-data')
            },

            canImport() {
                return !this.$auth.user.owner_id
            },

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            origins() {
                return [
                    {value: 'local', text: 'Local'},
                    {value: 'astral', text: 'ASTRAL'},
                ]
            },

            hasFilters() {
                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != 'name'
            }
        },

        created() {
            this.filtering.init(this)

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.owners = Array.isArray(resp.data.included.owners) ? {} : resp.data.included.owners
                this.allStatusOptions = resp.data.included.status_options
                this.statusOptions = {...this.allStatusOptions}
                delete this.statusOptions.archived
            })
        },

        mounted() {
            if (this.filters.name) {
                this.$refs.search.setFilter(this.filters.name.$text)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.name
                } else {
                    this.filters.name = {$text: val}
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        methods: {

            sortChange(data) {
                this.filtering.sortChange(data)
            },

            filterChange(data) {
                this.filtering.filterChange(data)
            },

            clearFilters() {
                this.ctrl.query.sort = ['name']
                this.filtering.clearFilters()
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            reload() {
                let filters = {$and: []}
                
                // Owner.
                if (this.filters.owner) {
                    let inOwner = {owner_id: {$in: this.filters.owner.map(id => parseInt(id)).filter(val => val) }}
                    filters.$and.push(inOwner)
                }

                // Status.
                if (this.filters.status) {
                    let inStatus = {status: {$in: this.filters.status }}
                    filters.$and.push(inStatus)
                }

                // Origin.
                if (this.filters.origin) {
                    let inOrigin = {origin: {$in: this.filters.origin }}
                    filters.$and.push(inOrigin)
                }

                // Search.
                if (this.filters.name) {
                    filters.$and.push({ name: this.filters.name})
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                this.ctrl.filter(filters)
            },

            canEdit(row) {
                return row.status != 'archived'
            },

            canDeleteArchive(row) {
                return this.$auth.hasPermission('admin.delete-personal-data')
            },
            
            mainActions() {
                let actions = []
                if (this.canImport) {
                    actions.push({
                    label: this.$t('common.import'),
                    handler: this.importCohorts,
                    })
                }
                if (this.canDeleteAll) {
                    actions.push({
                        label: this.$t('cohorts.outdated'),
                        handler: this.deleteOutdated,
                    })
                }
                return actions
            },

            importCohorts() {
                this.$router.push({ name: 'import-cohorts'})
            },

            deleteOutdated() {
                this.$router.push({ name: 'clear-cohorts'})
            },

            actions(row) {
                let actions = []
                if (row.status == 'draft' || row.status == 'active') {
                    actions.push({
                        label: this.$t('status.archive'),
                        handler: this.archive,
                        data: row
                    })
                }
                if (row.status == 'archived' && this.$auth.user.admin) {
                    actions.push({
                        label: this.$t('common.restore-archive'),
                        handler: this.restoreArchive,
                        data: row
                    })
                }
                if (row.status == 'archived' && this.canDeleteArchive(row)) {
                    actions.push({
                        label: this.$t('common.delete-archive'),
                        handler: this.deleteArchive,
                        data: row
                    })
                }
                return actions
            },

            deleteArchive(row) {
                this.ctrl.delete(row.id)
            },

            archive(row) {
                this.ctrl.modals.open('confirmArchive').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'archived'
                        this.ctrl.client.update(this.mapping.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            restoreArchive(row) {
                this.ctrl.modals.open('confirmRestore').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'active'
                        this.ctrl.client.update(this.mapping.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            statusColor(status) {
                return {
                    draft: 'primary',
                    active: 'success',
                    archived: 'secondary',
                }[status]
            },

            statusLabel(status) {
                const key = {
                    draft: 'inactive',
                    active: 'active',
                    archived: 'archived',
                }[status]
                return this.$t('status.'+key)
            },

            openLearners(row) {
                this.$router.push({ name: 'admin-cohort', params: { id: row.id, role: 'learner' } })
            },

            openInstructors(row) {
                this.$router.push({ name: 'admin-cohort', params: { id: row.id, role: 'instructor' } })
            },

            thenOpen(resp) {
                if (this.next) {
                    this.$router.push({ name: 'admin-cohort', params: { id: resp.data.data.id, role: 'learner' } })
                }
            },
        }
    }
</script>

<style scoped>

    .card >>> .el-table .cell {
        line-height: 1.5;
    }
    .card >>> td,
    .card >>> th {
        padding-right: 0;
    }
    .card >>> td.actions,
    .card >>> th.actions {
        padding-left: 0;
        padding-right: 1.5rem;
    }

</style>